<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useLibraryStore } from './library.store'
import { useUserStore } from '@/authentication/user.store'
import FolderAvatar from './FolderAvatar.vue'
import FolderSharedIcon from '@/assets/folder_shared.svg'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/tooltip'
import { getFolderOwnerUuid } from '@/library/library_helpers.ts'

const libraryStore = useLibraryStore()
const userStore = useUserStore()
const currentFolderHasCurrentContributors = libraryStore.currentFolderHasCurrentContributors
const currentAndPastContributorIds = computed(() =>
    Object.values(libraryStore.getContributorIdsForContent).filter((entry: {contributor: string, isFolderOwner: boolean}) => !entry.isFolderOwner)
)
const { currentFolder, doesUserOwnCurrentFolder } = storeToRefs(libraryStore)
const ownerUserId = computed(() => getFolderOwnerUuid(currentFolder.value))
const currentUserId = userStore.userId
const newContentCount = computed(() => {
  if (!currentFolder.value?.uuid || !doesUserOwnCurrentFolder.value) {
    return 0
  }
  return libraryStore.getNewContentCount(currentFolder.value.uuid)
})
const contributorUserIds = computed(() => {
  const contributors = currentFolder.value?.contributors || []
  const userId = currentUserId
  // If current user is a contributor, move them to the front
  if (userId && contributors.includes(userId)) {
    const otherContributors = contributors.filter((id) => id !== userId)
    return [userId, ...otherContributors]
  }

  return contributors
})
const infoText = "Contributors may add content to this folder but may not edit or remove content once it has been added."

function sendContributorTooltipEvent() {
  getAnalyticsTracker().sendEvent(EVENT_NAMES.LIBRARY.REVEALED_CONTRIBUTOR_TOOLTIP)
}

function getFolderMemberByUUID(UUID: string) {
  const folderMemberRoles = currentFolder.value?.folder_roles
  return folderMemberRoles?.find((member) => member.user_uuid === UUID)
}
const hasOverflowContributors = computed(() => contributorUserIds.value.length > 2)

const overflowCount = computed(() => contributorUserIds.value.length - 2)

const overflowCountLabel = computed(() =>
  `${overflowCount.value} more contributor${overflowCount.value > 1 ? 's' : ''}`
)

const overflowContributors = computed(() => {
  const overflowUserUUIDs = contributorUserIds.value.slice(2)
  return overflowUserUUIDs.map((uuid) => {
    const member = getFolderMemberByUUID(uuid)
    return member || {
      user_uuid: uuid,
      user_name: 'Unknown user',
      user_email: '',
      role: 'contributor'
    }
  })
})
</script>

<template>
  <div class="flex flex-col">
    <div class="flex items-center">
      <h1 class="inline-flex items-center overflow-x-hidden">
        <span v-if="currentFolderHasCurrentContributors" class="relative inline-block pr-2">
          <img :src="FolderSharedIcon" alt="Shared folder" class="shared-folder-icon w-[48px] max-w-none" />
        </span>
        <span v-else class="material-icons-round display-sm pr-2 text-display-xl text-gray-700">folder</span>
        <span class="header-md fs-exclude overflow-x-hidden text-ellipsis whitespace-nowrap font-medium">
          {{ currentFolder.name || 'Untitled Folder' }}
        </span>
      </h1>
      <div v-if="newContentCount > 0" class="ml-2 flex items-center gap-1.5">
        <span class="material-icons-round text-xs text-blue-500">circle</span>
        <span class="text-md font-medium text-blue-500">
          {{ newContentCount }} new {{ newContentCount === 1 ? 'contribution' : 'contributions' }}
        </span>
      </div>
    </div>
    <div
      v-if="contributorUserIds.length > 0 || currentAndPastContributorIds.length > 0"
      class="mt-2 flex items-center gap-4"
    >
      <div class="flex items-center gap-1" data-testid="owner-header">
        <span class="text-lg font-medium">Owner:</span>
        <FolderAvatar
          v-if="ownerUserId"
          :userId="ownerUserId"
          :folder="currentFolder"
          avatarSize="md"
        />
      </div>
      <div
        class="flex items-center gap-1"
        data-testid="contributor-header"
        v-if="contributorUserIds.length > 0"
      >
        <span class="text-lg font-medium">Contributors:</span>
        <div class="flex items-center">
          <div class="flex -space-x-3">
            <div class="relative z-10">
              <FolderAvatar
                v-if="contributorUserIds[0]"
                :userId="contributorUserIds[0]"
                :folder="currentFolder"
                avatarSize="md"
              />
            </div>
            <div class="relative z-0">
              <FolderAvatar
                v-if="contributorUserIds.length > 1 && contributorUserIds[1]"
                :userId="contributorUserIds[1]"
                :folder="currentFolder"
                avatarSize="md"
              />
            </div>
          </div>
          <TooltipProvider v-if="hasOverflowContributors" :delayDuration="200">
            <Tooltip side="top">
              <TooltipTrigger asChild>
                <span
                  data-testid="overflow-count"
                  class="fs-exclude cursor-help text-sm text-black outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                  tabindex="0"
                  :aria-label="overflowCountLabel"
                >
                  +{{ overflowCount }}
                </span>
              </TooltipTrigger>
              <TooltipContent class="tooltip-content fs-exclude max-w-[300px]">
                <div
                  v-for="contributor in overflowContributors"
                  :key="`overflow-contributor-${contributor?.user_uuid}`"
                  class="mb-2"
                >
                  <p>{{ contributor?.user_name || 'Unknown user' }}</p>
                  <p v-if="contributor?.user_email">{{ contributor?.user_email }}</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <span
        tabindex="0"
        :aria-label="infoText"
        class="cursor-help text-sm text-blue-400 underline decoration-dotted outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        v-tooltip.bottom="{ value: infoText, class: 'tooltip-style' }"
        @mouseenter="sendContributorTooltipEvent"
        v-if="contributorUserIds.length > 0"
        >What can a Contributor do?</span
      >
    </div>
  </div>
</template>
